// RosterDetailCard.js
// Extracted from SearchRosters.js so these are module-scope component
// definitions (created once) instead of being re-declared on every render
// of SearchRosters. Each component is wrapped in React.memo so a re-render
// of SearchRosters (e.g. snackbar/loading state changes) no longer forces
// every card in the list to unmount/remount.
import React, { useState, Suspense, lazy } from "react";
import PropTypes from "prop-types";
import {
  Badge,
  Box,
  Card,
  CardActions,
  CardContent,
  Collapse,
  Divider,
  Grid,
  IconButton,
  Link,
  Tooltip,
  Typography,
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import HistoryIcon from "@mui/icons-material/History";
import PersonIcon from "@mui/icons-material/Person";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PersonAddDisabledIcon from "@mui/icons-material/PersonAddDisabled";
import AddToPhotosIcon from "@mui/icons-material/AddToPhotos";
import InsertDriveFileRoundedIcon from "@mui/icons-material/InsertDriveFileRounded";
import DisabledByDefaultIcon from "@mui/icons-material/DisabledByDefault";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import SchoolIcon from "@mui/icons-material/School";
import FileOpenIcon from "@mui/icons-material/FileOpen";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ContactsIcon from "@mui/icons-material/Contacts";
import CancelIcon from "@mui/icons-material/Cancel";
import PreviewIcon from "@mui/icons-material/Preview";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { format } from "date-fns";
import {
  bounceAnimation,
  SubHeading,
  getRequestStatusIcon,
  getRosterStatusIcon,
  iconButtonStyle,
  getConditionalIconStyle,
} from "./searchRostersConstants";

// Code-split: TraineeDetailsGrid and the ClassRequestDrawer are both fairly
// heavy panels that only render once a user expands a card / opens the
// drawer, so they don't need to be in the initial bundle for this list.
const TraineeDetailsGrid = lazy(() => import("./TraineeDetailsGrid"));
const ClassRequestDrawer = lazy(
  () => import("components/ClassRequestDrawer/ClassRequestDrawer"),
);

// Shared wrapper for the very common "Tooltip whose title is a styled
// Typography" pattern used throughout this file. Replaces ~20 repeated
// Tooltip/Typography blocks with one component.
const InfoTooltip = ({ title, placement, children, onClick }) => (
  <Tooltip
    title={
      <Typography variant="body2" sx={{ fontSize: "1rem" }}>
        {title}
      </Typography>
    }
    placement={placement}
    arrow
    onClick={onClick}
  >
    {children}
  </Tooltip>
);
InfoTooltip.propTypes = {
  title: PropTypes.node,
  placement: PropTypes.string,
  children: PropTypes.element.isRequired,
  onClick: PropTypes.func,
};

// Reusable Tooltip + Typography wrapper, used by most of the simple fields below.
const TooltipTypography = ({ value }) => (
  <InfoTooltip title={value} placement="bottom-start">
    <Typography variant="subtitle1" gutterBottom>
      {value}
    </Typography>
  </InfoTooltip>
);
TooltipTypography.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

const RequisitionChip = React.memo(function RequisitionChip({
  reqId,
  onDelete,
  t,
}) {
  const [hovered, setHovered] = useState(false);

  const [id, flag] = reqId.split("-");
  const isAdhoc = flag === "1";

  const baseBgColor = isAdhoc ? "#FFE0B2" : "#E3F2FD";
  const hoverBgColor = isAdhoc ? "#FFD180" : "#D0E9FF";
  const textColor = isAdhoc ? "#BF360C" : "#084C9E";
  const iconColor = isAdhoc ? "#F57C00" : "#3299FF";
  const iconHoverColor = isAdhoc ? "#E65100" : "#0B75D1";

  return (
    <Box
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        px: 1.5,
        py: 0.5,
        mr: 0.5,
        backgroundColor: hovered ? hoverBgColor : baseBgColor,
        borderRadius: "16px",
        fontSize: "0.875rem",
        position: "relative",
        maxHeight: 32,
        transition: "all 0.3s ease",
        pl: 1.5,
        pr: hovered ? 4 : 1.5,
        color: textColor,
        cursor: "default",
      }}
    >
      <InfoTooltip
        title={
          isAdhoc
            ? t("tooltip.adhoc_requisition")
            : t("tooltip.linked_requisition")
        }
      >
        <Typography
          noWrap
          sx={{
            transition: "all 0.3s ease",
            overflow: "hidden",
            textOverflow: "ellipsis",
            fontWeight: 500,
            color: textColor,
          }}
        >
          {id}
        </Typography>
      </InfoTooltip>

      <InfoTooltip title={t("typography.remove_requisition_icon")}>
        <IconButton
          size="small"
          onClick={() => onDelete(reqId)}
          sx={{
            position: "absolute",
            right: 2,
            opacity: hovered ? 1 : 0,
            transform: hovered ? "translateX(0)" : "translateX(8px)",
            transition: "opacity 0.3s ease, transform 0.3s ease",
            padding: 0.5,
            color: hovered ? iconHoverColor : iconColor,
          }}
        >
          <CancelIcon fontSize="small" />
        </IconButton>
      </InfoTooltip>
    </Box>
  );
});
RequisitionChip.propTypes = {
  reqId: PropTypes.string.isRequired,
  onDelete: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

const RequisitionChips = React.memo(function RequisitionChips({
  data,
  index,
  onDelete,
  expandedRequisitionIndex,
  setExpandedRequisitionIndex,
  t,
}) {
  if (!data.RequisitionIds) return "NA";

  const requisitions = data.RequisitionIds.split(",").map((id) => id.trim());
  const isExpanded = expandedRequisitionIndex === index;

  const visibleRequisitions = isExpanded
    ? requisitions
    : requisitions.slice(0, 3);

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 0.5,
        alignItems: "center",
      }}
    >
      {visibleRequisitions.map((reqId, idx) => (
        <RequisitionChip
          key={`${reqId}-${idx}`}
          reqId={reqId}
          onDelete={(id) => onDelete(id, data.ClassRosterId)}
          t={t}
        />
      ))}

      {requisitions.length > 3 && (
        <InfoTooltip
          title={
            isExpanded
              ? t("typography.click_to_collapse")
              : t("typography.click_to_expand")
          }
          placement="right"
        >
          <IconButton
            size="small"
            onClick={() =>
              setExpandedRequisitionIndex(isExpanded ? null : index)
            }
          >
            <Typography
              sx={{
                cursor: "pointer",
                color: "#3299FF",
                fontSize: "1rem",
              }}
            >
              {isExpanded
                ? t("iconbutton.all_requisition_ids")
                : `+${requisitions.length - 3}`}
            </Typography>
          </IconButton>
        </InfoTooltip>
      )}
    </Box>
  );
});
RequisitionChips.propTypes = {
  data: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  onDelete: PropTypes.func.isRequired,
  expandedRequisitionIndex: PropTypes.number,
  setExpandedRequisitionIndex: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

const AddRequisitionIcon = React.memo(function AddRequisitionIcon({
  data,
  handleRequisitionDialogOpen,
  t,
}) {
  return (
    <InfoTooltip title={t("typography.add_requisition")}>
      <Badge
        badgeContent={data.TotalRequisition || 0}
        color="error"
        overlap="circular"
        sx={{
          "& .MuiBadge-badge": {
            fontSize: "0.75rem",
            minWidth: "20px",
            height: "20px",
          },
        }}
      >
        <IconButton
          sx={iconButtonStyle}
          onClick={() =>
            handleRequisitionDialogOpen(data.ClassRosterId, data.RequisitionIds)
          }
        >
          <AddToPhotosIcon sx={{ fontSize: "1.75rem" }} />
        </IconButton>
      </Badge>
    </InfoTooltip>
  );
});
AddRequisitionIcon.propTypes = {
  data: PropTypes.object.isRequired,
  handleRequisitionDialogOpen: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

const FileAttachmentIcon = React.memo(function FileAttachmentIcon({
  data,
  handleAttachmentDialogOpen,
  t,
}) {
  return (
    <InfoTooltip
      title={t("typography.roster_attachment")}
      onClick={() =>
        handleAttachmentDialogOpen(data.ClassRequestId, data.ClassRosterId)
      }
    >
      <Badge
        badgeContent={data.TotalAttachment || 0}
        color="error"
        overlap="circular"
        sx={{
          "& .MuiBadge-badge": {
            fontSize: "0.75rem",
            minWidth: "20px",
            height: "20px",
          },
        }}
      >
        <IconButton sx={iconButtonStyle}>
          <CloudUploadIcon sx={{ fontSize: "1.75rem" }} />
        </IconButton>
      </Badge>
    </InfoTooltip>
  );
});
FileAttachmentIcon.propTypes = {
  data: PropTypes.object.isRequired,
  handleAttachmentDialogOpen: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

const OGChecklistIcon = React.memo(function OGChecklistIcon({ data, t }) {
  const hasChecklist = !!data.OGChecklistURL;

  return (
    <InfoTooltip
      title={
        hasChecklist
          ? t("typography.og_checklist")
          : t("typography.og_checklist_not_available")
      }
    >
      <IconButton
        sx={getConditionalIconStyle(hasChecklist)}
        onClick={() => {
          if (hasChecklist) {
            window.open(data.OGChecklistURL, "_blank");
          }
        }}
      >
        {hasChecklist ? (
          <FileOpenIcon sx={{ fontSize: "1.75rem" }} />
        ) : (
          <InsertDriveFileRoundedIcon sx={{ fontSize: "1.75rem" }} />
        )}
      </IconButton>
    </InfoTooltip>
  );
});
OGChecklistIcon.propTypes = {
  data: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
};

const ViewTraineeIcon = React.memo(function ViewTraineeIcon({
  data,
  index,
  expandedViewTraineeIndex,
  setExpandedViewTraineeIndex,
  t,
}) {
  const hasTrainees = data.TotalTrainee > 0;

  return (
    <InfoTooltip
      title={
        hasTrainees
          ? t("typography.view_trainee")
          : t("typography.no_trainee_assigned")
      }
    >
      <Badge
        badgeContent={data.TotalTrainee || 0}
        color="error"
        overlap="circular"
        max={999}
        sx={{
          "& .MuiBadge-badge": {
            fontSize: "0.75rem",
            minWidth: "20px",
            height: "20px",
          },
        }}
      >
        <Box
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <IconButton
            sx={getConditionalIconStyle(hasTrainees)}
            onClick={() => {
              if (hasTrainees) {
                setExpandedViewTraineeIndex(
                  expandedViewTraineeIndex === index ? null : index,
                );
              }
            }}
          >
            {hasTrainees ? (
              <PersonIcon sx={{ fontSize: "1.75rem" }} />
            ) : (
              <PersonOffIcon sx={{ fontSize: "1.75rem" }} />
            )}
            {expandedViewTraineeIndex === index && (
              <ExpandMoreIcon
                sx={{
                  fontSize: "1.75rem",
                  animation: `${bounceAnimation} 1s infinite`,
                  position: "absolute",
                  top: "70%",
                }}
              />
            )}
          </IconButton>
        </Box>
      </Badge>
    </InfoTooltip>
  );
});
ViewTraineeIcon.propTypes = {
  data: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  expandedViewTraineeIndex: PropTypes.number,
  setExpandedViewTraineeIndex: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

const CancelRosterIcon = React.memo(function CancelRosterIcon({
  data,
  handleCancelRoster,
  t,
}) {
  const canCancel = [1, 4].includes(data.RosterStatusId);

  return (
    <InfoTooltip
      title={
        canCancel ? t("typography.cancel_roster") : t("typography.can_t_cancel")
      }
    >
      <IconButton
        sx={getConditionalIconStyle(
          canCancel,
          "red",
          "grey",
          "#CC0000",
          "#6c757d",
        )}
        onClick={() => {
          if (canCancel) {
            handleCancelRoster(data.ClassRosterId);
          }
        }}
      >
        <DisabledByDefaultIcon sx={{ fontSize: "1.75rem" }} />
      </IconButton>
    </InfoTooltip>
  );
});
CancelRosterIcon.propTypes = {
  data: PropTypes.object.isRequired,
  handleCancelRoster: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

const StatusIcon = React.memo(function StatusIcon({
  label,
  status,
  getIcon,
  t,
}) {
  const displayStatus =
    status === "Active" || status === "In Progress"
      ? "Active / In Progress"
      : status;

  return (
    <InfoTooltip title={displayStatus} placement="bottom-start">
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <SubHeading>{label}</SubHeading>
        <Box
          component="img"
          src={getIcon(status)}
          alt={`${status} icon`}
          sx={{
            width: 24,
            height: 24,
            cursor: "pointer",
            mb: 0.5,
          }}
        />
      </Box>
    </InfoTooltip>
  );
});
StatusIcon.propTypes = {
  label: PropTypes.node.isRequired,
  status: PropTypes.string,
  getIcon: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

const LearnPointClassRosterIcon = React.memo(function LearnPointClassRosterIcon({
  data,
  t,
}) {
  return (
    <InfoTooltip title={t("typography.learnpoint_class_roster")}>
      <IconButton
        sx={iconButtonStyle}
        onClick={() => {
          if (data.ClassRosterId) {
            const url = `/learnpoint/infoSection/ClassRoster?classRosterId=${data.ClassRosterId}`;
            window.open(url, "_blank");
          }
        }}
      >
        <SchoolIcon sx={{ fontSize: "1.75rem" }} />
      </IconButton>
    </InfoTooltip>
  );
});
LearnPointClassRosterIcon.propTypes = {
  data: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
};

const ContactRosterIcon = React.memo(function ContactRosterIcon({ data, t }) {
  return (
    <InfoTooltip title={t("typography.contact_roster")}>
      <IconButton
        sx={iconButtonStyle}
        onClick={() => {
          if (data.ClassRosterId) {
            window.open(`/contact-roster/${data.ClassRosterId}`, "_blank");
          }
        }}
      >
        <ContactsIcon sx={{ fontSize: "1.75rem" }} />
      </IconButton>
    </InfoTooltip>
  );
});
ContactRosterIcon.propTypes = {
  data: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
};

const AssignTraineeIcon = React.memo(function AssignTraineeIcon({
  data,
  navigate,
  t,
}) {
  const canAssign = !data.AssignTraineesLockTimeStamp;

  return (
    <InfoTooltip
      title={
        canAssign
          ? t("typography.assign_trainee")
          : t("typography.can_t_assign_trainee_roster_locked")
      }
    >
      <IconButton
        sx={getConditionalIconStyle(canAssign)}
        onClick={() => {
          if (canAssign) {
            navigate(`/home/assignTrainees`, {
              state: {
                classRequestId: data.ClassRequestId,
                classRosterId: data.ClassRosterId,
              },
            });
          }
        }}
      >
        {canAssign ? (
          <PersonAddIcon sx={{ fontSize: "1.75rem" }} />
        ) : (
          <PersonAddDisabledIcon sx={{ fontSize: "1.75rem" }} />
        )}
      </IconButton>
    </InfoTooltip>
  );
});
AssignTraineeIcon.propTypes = {
  data: PropTypes.object.isRequired,
  navigate: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

const RosterHistoryIcon = React.memo(function RosterHistoryIcon({
  data,
  handleHistoryDialogOpen,
  t,
}) {
  return (
    <InfoTooltip
      title={t("typography.roster_history")}
      onClick={() => handleHistoryDialogOpen(data.ClassRosterId)}
    >
      <IconButton sx={iconButtonStyle}>
        <HistoryIcon sx={{ fontSize: "1.75rem" }} />
      </IconButton>
    </InfoTooltip>
  );
});
RosterHistoryIcon.propTypes = {
  data: PropTypes.object.isRequired,
  handleHistoryDialogOpen: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

const CloneRosterIcon = React.memo(function CloneRosterIcon({
  data,
  navigate,
  t,
}) {
  return (
    <InfoTooltip title={t("typography.clone_roster")}>
      <IconButton
        sx={iconButtonStyle}
        onClick={() => {
          if (data.ClassRosterId && data.ClassRequestId) {
            const isRosterClone = true;
            navigate(
              `/home/rosterconfig?type=clone&classRosterId=${data.ClassRosterId}&classRequestId=${data.ClassRequestId}&data=${isRosterClone}`,
            );
          }
        }}
      >
        <FileCopyIcon sx={{ fontSize: "1.75rem" }} />
      </IconButton>
    </InfoTooltip>
  );
});
CloneRosterIcon.propTypes = {
  data: PropTypes.object.isRequired,
  navigate: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

const RosterConfigIcon = React.memo(function RosterConfigIcon({
  data,
  navigate,
  t,
}) {
  return (
    <InfoTooltip title={t("typography.roster_configuration")}>
      <IconButton
        sx={iconButtonStyle}
        onClick={() => {
          if (data.ClassRosterId && data.ClassRequestId) {
            const isRosterClone = false;
            navigate(
              `/home/rosterconfig?type=edit&classRosterId=${data.ClassRosterId}&classRequestId=${data.ClassRequestId}&data=${isRosterClone}`,
            );
          }
        }}
      >
        <SettingsIcon sx={{ fontSize: "1.75rem" }} />
      </IconButton>
    </InfoTooltip>
  );
});
RosterConfigIcon.propTypes = {
  data: PropTypes.object.isRequired,
  navigate: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

const ClassRequestDetailsIcon = React.memo(function ClassRequestDetailsIcon({
  data,
  handleOpen,
  t,
}) {
  return (
    <InfoTooltip title={t("typography.classRequest_details")}>
      <IconButton
        sx={iconButtonStyle}
        onClick={() => handleOpen(data.ClassRequestId)}
      >
        <PreviewIcon sx={{ fontSize: "1.75rem" }} />
      </IconButton>
    </InfoTooltip>
  );
});
ClassRequestDetailsIcon.propTypes = {
  data: PropTypes.object.isRequired,
  handleOpen: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

const ActionIconsContainer = React.memo(function ActionIconsContainer({
  data,
  index,
  expandedViewTraineeIndex,
  setExpandedViewTraineeIndex,
  selectedId,
  isDialogOpen,
  setIsDialogOpen,
  handleRequisitionDialogOpen,
  handleAttachmentDialogOpen,
  handleHistoryDialogOpen,
  handleCancelRoster,
  handleOpen,
  navigate,
  t,
}) {
  return (
    <Box
      sx={{
        display: "flex",
        gap: 1.5,
        backgroundColor: "#f5f5f5",
        borderRadius: "4px",
        padding: "0.5rem",
        boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
        opacity: 0.95,
        position: "relative",
      }}
    >
      <AddRequisitionIcon
        data={data}
        handleRequisitionDialogOpen={handleRequisitionDialogOpen}
        t={t}
      />
      <FileAttachmentIcon
        data={data}
        handleAttachmentDialogOpen={handleAttachmentDialogOpen}
        t={t}
      />
      <OGChecklistIcon data={data} t={t} />
      <LearnPointClassRosterIcon data={data} t={t} />
      <ContactRosterIcon data={data} t={t} />
      <ViewTraineeIcon
        data={data}
        index={index}
        expandedViewTraineeIndex={expandedViewTraineeIndex}
        setExpandedViewTraineeIndex={setExpandedViewTraineeIndex}
        t={t}
      />
      <AssignTraineeIcon data={data} navigate={navigate} t={t} />
      <RosterHistoryIcon
        data={data}
        handleHistoryDialogOpen={handleHistoryDialogOpen}
        t={t}
      />
      <CloneRosterIcon data={data} navigate={navigate} t={t} />
      <RosterConfigIcon data={data} navigate={navigate} t={t} />
      <CancelRosterIcon
        data={data}
        handleCancelRoster={handleCancelRoster}
        t={t}
      />
      <ClassRequestDetailsIcon data={data} handleOpen={handleOpen} t={t} />
      {selectedId && (
        <Suspense fallback={null}>
          <ClassRequestDrawer
            classRequestId={selectedId}
            open={isDialogOpen}
            onClose={() => setIsDialogOpen(false)}
            classRosterId={data.ClassRosterId}
          />
        </Suspense>
      )}
    </Box>
  );
});
ActionIconsContainer.propTypes = {
  data: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  expandedViewTraineeIndex: PropTypes.number,
  setExpandedViewTraineeIndex: PropTypes.func.isRequired,
  selectedId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isDialogOpen: PropTypes.bool,
  setIsDialogOpen: PropTypes.func.isRequired,
  handleRequisitionDialogOpen: PropTypes.func.isRequired,
  handleAttachmentDialogOpen: PropTypes.func.isRequired,
  handleHistoryDialogOpen: PropTypes.func.isRequired,
  handleCancelRoster: PropTypes.func.isRequired,
  handleOpen: PropTypes.func.isRequired,
  navigate: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

const RosterActions = React.memo(function RosterActions({
  data,
  index,
  expandedViewTraineeIndex,
  setExpandedViewTraineeIndex,
  selectedId,
  isDialogOpen,
  setIsDialogOpen,
  handleRequisitionDialogOpen,
  handleAttachmentDialogOpen,
  handleHistoryDialogOpen,
  handleCancelRoster,
  handleOpen,
  navigate,
  t,
}) {
  return (
    <CardActions sx={{ py: 2 }}>
      <Grid container item xs={1.5} sx={{ pl: 1 }}>
        <StatusIcon
          label={t("requestStatus")}
          status={data.RequestStatus}
          getIcon={getRequestStatusIcon}
          t={t}
        />
      </Grid>
      <Grid container item xs={1.5} sx={{ pl: 1 }}>
        <StatusIcon
          label={t("rosterStatus")}
          status={data.RosterStatus}
          getIcon={getRosterStatusIcon}
          t={t}
        />
      </Grid>
      <Grid item xs={9}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            gap: 1,
          }}
        >
          <ActionIconsContainer
            data={data}
            index={index}
            expandedViewTraineeIndex={expandedViewTraineeIndex}
            setExpandedViewTraineeIndex={setExpandedViewTraineeIndex}
            selectedId={selectedId}
            isDialogOpen={isDialogOpen}
            setIsDialogOpen={setIsDialogOpen}
            handleRequisitionDialogOpen={handleRequisitionDialogOpen}
            handleAttachmentDialogOpen={handleAttachmentDialogOpen}
            handleHistoryDialogOpen={handleHistoryDialogOpen}
            handleCancelRoster={handleCancelRoster}
            handleOpen={handleOpen}
            navigate={navigate}
            t={t}
          />
        </Box>
      </Grid>
    </CardActions>
  );
});
RosterActions.propTypes = {
  data: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  expandedViewTraineeIndex: PropTypes.number,
  setExpandedViewTraineeIndex: PropTypes.func.isRequired,
  selectedId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isDialogOpen: PropTypes.bool,
  setIsDialogOpen: PropTypes.func.isRequired,
  handleRequisitionDialogOpen: PropTypes.func.isRequired,
  handleAttachmentDialogOpen: PropTypes.func.isRequired,
  handleHistoryDialogOpen: PropTypes.func.isRequired,
  handleCancelRoster: PropTypes.func.isRequired,
  handleOpen: PropTypes.func.isRequired,
  navigate: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

const RosterIdField = React.memo(function RosterIdField({ data, t }) {
  return (
    <Grid item xs={0.75}>
      <SubHeading>{t("subheading.roster_id")}</SubHeading>
      <TooltipTypography value={Number(data.ClassRosterId)} />
    </Grid>
  );
});
RosterIdField.propTypes = { data: PropTypes.object.isRequired, t: PropTypes.func.isRequired };

const WaveField = React.memo(function WaveField({ data, t }) {
  return (
    <Grid item xs={1}>
      <SubHeading>{t("subheading.wave_")}</SubHeading>
      <TooltipTypography value={data.Wave} />
    </Grid>
  );
});
WaveField.propTypes = { data: PropTypes.object.isRequired, t: PropTypes.func.isRequired };

const SiteField = React.memo(function SiteField({ data, t }) {
  return (
    <Grid item xs={2}>
      <SubHeading>{t("subheading.site")}</SubHeading>
      <TooltipTypography value={data.SiteName} />
    </Grid>
  );
});
SiteField.propTypes = { data: PropTypes.object.isRequired, t: PropTypes.func.isRequired };

const ProjectToggle = React.memo(function ProjectToggle({
  projects,
  isExpanded,
  onToggle,
  t,
}) {
  if (isExpanded) {
    return (
      <>
        {projects.map((item, idx) => (
          <Grid item key={`${idx}-${item}`}>
            <Typography variant="subtitle1">{item}</Typography>
          </Grid>
        ))}
        <InfoTooltip title={t("typography.click_to_collapse")} placement="right">
          <IconButton
            sx={{
              cursor: "pointer",
              color: "#3299FF",
              background: "none",
              border: "none",
              padding: 0,
              font: "inherit",
            }}
            onClick={(event) => {
              event.stopPropagation();
              onToggle();
            }}
          >
            {t("iconbutton.all_projects")}
          </IconButton>
        </InfoTooltip>
      </>
    );
  }

  return (
    <>
      {projects[0]}{" "}
      <InfoTooltip title={t("typography.click_to_expand")} placement="right">
        <IconButton
          sx={{
            cursor: "pointer",
            color: "#3299FF",
            background: "none",
            border: "none",
            padding: 0,
            font: "inherit",
          }}
          onClick={(event) => {
            event.stopPropagation();
            onToggle();
          }}
        >
          {t("iconbutton._")}
          {projects.length - 1}
        </IconButton>
      </InfoTooltip>
    </>
  );
});
ProjectToggle.propTypes = {
  projects: PropTypes.arrayOf(PropTypes.string).isRequired,
  isExpanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

const ProjectField = React.memo(function ProjectField({
  data,
  index,
  expandedProjectIndex,
  setExpandedProjectIndex,
  t,
}) {
  const projects = data.ProjectName.split(",");
  const isExpanded = expandedProjectIndex === index;
  const hasMultipleProjects = projects.length > 1;

  return (
    <Grid item xs={2.5}>
      <SubHeading>{t("subheading.project")}</SubHeading>
      <InfoTooltip
        title={
          isExpanded
            ? projects.map((item, idx) => (
                <Grid item key={`${idx}-${item}`}>
                  <Typography variant="subtitle1">{item}</Typography>
                </Grid>
              ))
            : projects[0]
        }
        placement="bottom-start"
      >
        <Typography variant="subtitle1" gutterBottom>
          {hasMultipleProjects ? (
            <ProjectToggle
              projects={projects}
              isExpanded={isExpanded}
              onToggle={() =>
                setExpandedProjectIndex(isExpanded ? null : index)
              }
              t={t}
            />
          ) : (
            data.ProjectName
          )}
        </Typography>
      </InfoTooltip>
    </Grid>
  );
});
ProjectField.propTypes = {
  data: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  expandedProjectIndex: PropTypes.number,
  setExpandedProjectIndex: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

const TrainerNameField = React.memo(function TrainerNameField({ data, t }) {
  return (
    <Grid item xs={2}>
      <SubHeading>{t("subheading.trainer_name")}</SubHeading>
      <InfoTooltip title={data.TrainerName} placement="bottom-start">
        <Typography variant="subtitle1" gutterBottom>
          <Link
            href={`mailto:${data.TrainerEmail}`}
            sx={{ color: "#000000", textDecoration: "none" }}
          >
            {data.TrainerName}
          </Link>
        </Typography>
      </InfoTooltip>
    </Grid>
  );
});
TrainerNameField.propTypes = { data: PropTypes.object.isRequired, t: PropTypes.func.isRequired };

const ProductionDateField = React.memo(function ProductionDateField({
  data,
  t,
}) {
  const formattedDate = data.ProductionDate
    ? format(new Date(data.ProductionDate), "dd-MMM-yyyy")
    : t("typography.na");

  return (
    <Grid item xs={1.25}>
      <SubHeading>{t("subheading.production_date")}</SubHeading>
      <TooltipTypography value={formattedDate} />
    </Grid>
  );
});
ProductionDateField.propTypes = { data: PropTypes.object.isRequired, t: PropTypes.func.isRequired };

const PlannedLockDateField = React.memo(function PlannedLockDateField({
  data,
  t,
}) {
  const formattedDate = data.RosterLockDate
    ? format(new Date(data.RosterLockDate), "dd-MMM-yyyy")
    : t("typography.na");

  return (
    <Grid item xs={1.5}>
      <SubHeading>{t("subheading.planned_lock_date")}</SubHeading>
      <TooltipTypography value={formattedDate} />
    </Grid>
  );
});
PlannedLockDateField.propTypes = { data: PropTypes.object.isRequired, t: PropTypes.func.isRequired };

const ClassTypeField = React.memo(function ClassTypeField({ data, t }) {
  return (
    <Grid item xs={1}>
      <SubHeading>{t("subheading.class_type")}</SubHeading>
      <TooltipTypography value={data.ClassType} />
    </Grid>
  );
});
ClassTypeField.propTypes = { data: PropTypes.object.isRequired, t: PropTypes.func.isRequired };

const ActualLockDateTimeField = React.memo(function ActualLockDateTimeField({
  data,
  t,
}) {
  const formattedDateTime = data.AssignTraineesLockTimeStamp
    ? format(
        new Date(data.AssignTraineesLockTimeStamp),
        "dd-MMM-yyyy hh:mm:ss a",
      )
    : t("typography.na");

  return (
    <Grid item xs={1.75}>
      <SubHeading>{t("subheading.actual_lock_date_time")}</SubHeading>
      <TooltipTypography value={formattedDateTime} />
    </Grid>
  );
});
ActualLockDateTimeField.propTypes = { data: PropTypes.object.isRequired, t: PropTypes.func.isRequired };

const RequisitionIdsField = React.memo(function RequisitionIdsField({
  data,
  index,
  handleDelete,
  expandedRequisitionIndex,
  setExpandedRequisitionIndex,
  t,
}) {
  return (
    <Grid item xs={1.75}>
      <SubHeading>{t("subheading.requisition_id_s_")}</SubHeading>
      <Box sx={{ py: 0.5 }}>
        <RequisitionChips
          data={data}
          index={index}
          onDelete={handleDelete}
          expandedRequisitionIndex={expandedRequisitionIndex}
          setExpandedRequisitionIndex={setExpandedRequisitionIndex}
          t={t}
        />
      </Box>
    </Grid>
  );
});
RequisitionIdsField.propTypes = {
  data: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  handleDelete: PropTypes.func.isRequired,
  expandedRequisitionIndex: PropTypes.number,
  setExpandedRequisitionIndex: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

const AssignedTraineesField = React.memo(function AssignedTraineesField({
  data,
  t,
}) {
  const tooltipText = `${t("typography.total_")}${Number(
    data.TotalTrainee,
  )}${t("typography._active_")} ${Number(data.TotalActiveTrainee)}${t(
    "typography._ftrr_",
  )} ${Number(data.TotalFTRRCount)}${t("typography._e2p_")} ${Number(
    data.TotalE2PCount,
  )}`;

  return (
    <Grid item xs={2.5}>
      <SubHeading>{t("subheading.assigned_trainees")}</SubHeading>
      <InfoTooltip title={tooltipText} placement="bottom-start">
        <Typography variant="subtitle1" gutterBottom>
          {t("typography.total_")} {Number(data.TotalTrainee)}{" "}
          {t("typography._")}{" "}
          <Box component="span" sx={{ color: "#3299FF" }}>
            Active: {Number(data.TotalActiveTrainee)}
          </Box>{" "}
          {t("typography._")}{" "}
          <Box component="span" sx={{ color: "#FFC107" }}>
            FTRR: {Number(data.TotalFTRRCount)}
          </Box>{" "}
          {t("typography._")}{" "}
          <Box component="span" sx={{ color: "#4CAF50" }}>
            E2P: {Number(data.TotalE2PCount)}
          </Box>
        </Typography>
      </InfoTooltip>
    </Grid>
  );
});
AssignedTraineesField.propTypes = { data: PropTypes.object.isRequired, t: PropTypes.func.isRequired };

const OGNameField = React.memo(function OGNameField({ data, t }) {
  return (
    <Grid item xs={1.5}>
      <SubHeading>{t("subheading.og_name")}</SubHeading>
      <InfoTooltip title={data.OGName} placement="bottom-start">
        <Typography variant="subtitle1" gutterBottom>
          <Link
            href={`mailto:${data.OGEmail}`}
            sx={{ color: "#000000", textDecoration: "none" }}
          >
            {data.OGName}
          </Link>
        </Typography>
      </InfoTooltip>
    </Grid>
  );
});
OGNameField.propTypes = { data: PropTypes.object.isRequired, t: PropTypes.func.isRequired };

const FDONameField = React.memo(function FDONameField({ data, t }) {
  return (
    <Grid item xs={1.5}>
      <SubHeading>{t("subheading.fdo_name")}</SubHeading>
      <InfoTooltip title={data.FDOName} placement="bottom-start">
        <Typography variant="subtitle1" gutterBottom>
          <Link
            href={`mailto:${data.FDOEmail}`}
            sx={{ color: "#000000", textDecoration: "none" }}
          >
            {data.FDOName}
          </Link>
        </Typography>
      </InfoTooltip>
    </Grid>
  );
});
FDONameField.propTypes = { data: PropTypes.object.isRequired, t: PropTypes.func.isRequired };

const formatCreatedDate = (dateString) => {
  if (!dateString) return "NA";
  try {
    const date = new Date(dateString);
    return isNaN(date) ? "NA" : format(date, "dd-MMM-yyyy hh:mm:ss a");
  } catch {
    return "NA";
  }
};

const CreatedByDateTimeField = React.memo(function CreatedByDateTimeField({
  data,
  t,
}) {
  const displayText = `${data.CreatedBy || "NA"} on ${formatCreatedDate(
    data.CreatedDate,
  )}`;

  return (
    <Grid item xs={3}>
      <SubHeading>{t("subheading.created_by_date_time")}</SubHeading>
      <TooltipTypography value={displayText} />
    </Grid>
  );
});
CreatedByDateTimeField.propTypes = { data: PropTypes.object.isRequired, t: PropTypes.func.isRequired };

export const TraineeDetailsCollapse = React.memo(function TraineeDetailsCollapse({
  data,
  index,
  expandedViewTraineeIndex,
  updateTraineeCounts,
}) {
  return (
    <Collapse
      unmountOnExit
      timeout="auto"
      in={expandedViewTraineeIndex === index}
    >
      <CardContent sx={{ paddingTop: 0 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
          </Grid>
          <Grid item xs={12}>
            <Suspense fallback={null}>
              <TraineeDetailsGrid
                classRosterId={String(data.ClassRosterId)}
                classRequestId={String(data.ClassRequestId)}
                parentClientCode={data.ParentClientCode}
                assignTraineesLockTimeStamp={data.AssignTraineesLockTimeStamp}
                updateTraineeCounts={updateTraineeCounts}
                totalProductionSchedule={data.TotalProductionSchedule || 0}
              />
            </Suspense>
          </Grid>
        </Grid>
      </CardContent>
    </Collapse>
  );
});
TraineeDetailsCollapse.propTypes = {
  data: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  expandedViewTraineeIndex: PropTypes.number,
  updateTraineeCounts: PropTypes.func.isRequired,
};

const RosterDetailCard = React.memo(function RosterDetailCard({
  data,
  index,
  expandedProjectIndex,
  setExpandedProjectIndex,
  expandedViewTraineeIndex,
  setExpandedViewTraineeIndex,
  expandedRequisitionIndex,
  setExpandedRequisitionIndex,
  selectedId,
  isDialogOpen,
  setIsDialogOpen,
  handleRequisitionDialogOpen,
  handleAttachmentDialogOpen,
  handleHistoryDialogOpen,
  handleCancelRoster,
  handleOpen,
  handleDelete,
  updateTraineeCounts,
  navigate,
  t,
}) {
  return (
    <Card
      variant="outlined"
      sx={{
        marginBottom: 2,
        border: "1px solid #e0e0e0",
      }}
    >
      <CardContent sx={{ paddingBottom: "0" }}>
        {/* Split into two explicit 12-column rows instead of relying on
            implicit MUI Grid wrap arithmetic (fragile - any width change to
            one field could silently shift where the row breaks). */}
        <Grid container spacing={1} alignItems="top">
          <Grid container item xs={12} spacing={1}>
            <RosterIdField data={data} t={t} />
            <WaveField data={data} t={t} />
            <ProjectField
              data={data}
              index={index}
              expandedProjectIndex={expandedProjectIndex}
              setExpandedProjectIndex={setExpandedProjectIndex}
              t={t}
            />
            <SiteField data={data} t={t} />
            <TrainerNameField data={data} t={t} />
            <ProductionDateField data={data} t={t} />
            <PlannedLockDateField data={data} t={t} />
            <ClassTypeField data={data} t={t} />
          </Grid>
          <Grid container item xs={12} spacing={1}>
            <ActualLockDateTimeField data={data} t={t} />
            <RequisitionIdsField
              data={data}
              index={index}
              handleDelete={handleDelete}
              expandedRequisitionIndex={expandedRequisitionIndex}
              setExpandedRequisitionIndex={setExpandedRequisitionIndex}
              t={t}
            />
            <AssignedTraineesField data={data} t={t} />
            <OGNameField data={data} t={t} />
            <FDONameField data={data} t={t} />
            <CreatedByDateTimeField data={data} t={t} />
          </Grid>
        </Grid>
      </CardContent>
      <RosterActions
        data={data}
        index={index}
        expandedViewTraineeIndex={expandedViewTraineeIndex}
        setExpandedViewTraineeIndex={setExpandedViewTraineeIndex}
        selectedId={selectedId}
        isDialogOpen={isDialogOpen}
        setIsDialogOpen={setIsDialogOpen}
        handleRequisitionDialogOpen={handleRequisitionDialogOpen}
        handleAttachmentDialogOpen={handleAttachmentDialogOpen}
        handleHistoryDialogOpen={handleHistoryDialogOpen}
        handleCancelRoster={handleCancelRoster}
        handleOpen={handleOpen}
        navigate={navigate}
        t={t}
      />
      <TraineeDetailsCollapse
        data={data}
        index={index}
        expandedViewTraineeIndex={expandedViewTraineeIndex}
        updateTraineeCounts={updateTraineeCounts}
      />
    </Card>
  );
});

RosterDetailCard.propTypes = {
  data: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  expandedProjectIndex: PropTypes.number,
  setExpandedProjectIndex: PropTypes.func.isRequired,
  expandedViewTraineeIndex: PropTypes.number,
  setExpandedViewTraineeIndex: PropTypes.func.isRequired,
  expandedRequisitionIndex: PropTypes.number,
  setExpandedRequisitionIndex: PropTypes.func.isRequired,
  selectedId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isDialogOpen: PropTypes.bool,
  setIsDialogOpen: PropTypes.func.isRequired,
  handleRequisitionDialogOpen: PropTypes.func.isRequired,
  handleAttachmentDialogOpen: PropTypes.func.isRequired,
  handleHistoryDialogOpen: PropTypes.func.isRequired,
  handleCancelRoster: PropTypes.func.isRequired,
  handleOpen: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
  updateTraineeCounts: PropTypes.func.isRequired,
  navigate: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

export default RosterDetailCard;
